import { SettingKey } from 'data/ppoints/constants';
import { DEFAULT_PROVIDER_COUNT, PROVIDER_COUNT_BY_COUNTRY } from 'data/ppoints/provider-counts';
import { mapAggregatorToProvider } from 'features/shipping-provider/utils/mapAggregatorToProvider';
import { usePPointsFeatures } from 'hooks/ppoints/usePPointsFeatures';
import { useShopifyLocationsSWR } from 'hooks/shopify/swr/useShopifyLocationsSWR';
import { useAggregatorsSWR } from 'hooks/useAggregatorsSWR';
import { useCountries } from 'hooks/useCountries';
import useSettings from 'hooks/useSettings';
import { CountryWithAggregatedProviders } from 'models/ppoints/CountryWithAggregatedProviders';
import { PackageProvider } from 'models/ppoints/PackageProvider';
import { ProviderToCountrySetting } from 'models/ppoints/settings/SettingsToEcom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PackageProviderFeature } from '../../../../pb/responses/v1/package_provider_features_response_pb';

/** Country whose `provider_to_country` entry lists the most providers. Keyed on country name. */
const getMostProvidersCountry = (providers: ProviderToCountrySetting): string => {
    let maxLength = 0;
    let countryWithMost = '';

    Object.entries(providers).forEach(([country, providerArray]) => {
        if (providerArray.length > maxLength) {
            maxLength = providerArray.length;
            countryWithMost = country;
        }
    });

    return countryWithMost;
};

/**
 * Reports drift between PROVIDER_COUNT_BY_COUNTRY and reality, and prints a corrected object to
 * paste back. Development only — a wrong count costs at most one layout shift in production, which
 * is not worth a console message to a merchant.
 */
const useProviderCountDriftWarning = (countries: CountryWithAggregatedProviders[], shopCountry?: string) => {
    // The map is keyed on uppercase ISO codes. If the shop's country arrives in another shape, every
    // lookup misses in silence and the first-visit skeleton quietly falls back to the default.
    useEffect(() => {
        if (!import.meta.env.DEV || !shopCountry) return;
        if (PROVIDER_COUNT_BY_COUNTRY[shopCountry.toUpperCase()] !== undefined) return;

        console.warn(
            `[provider-counts] shop country ${JSON.stringify(shopCountry)} has no entry in ` +
                `PROVIDER_COUNT_BY_COUNTRY, so a first-time visitor's skeleton falls back to ` +
                `${DEFAULT_PROVIDER_COUNT}. Expected an uppercase ISO code such as "HU".`
        );
    }, [shopCountry]);

    useEffect(() => {
        if (!import.meta.env.DEV || countries.length === 0) return;

        const actual: Record<string, number> = {};
        countries.forEach((country) => {
            actual[country.country_code.toUpperCase()] = country.packageproviders.length;
        });

        const drifted = Object.keys(actual).filter((code) => PROVIDER_COUNT_BY_COUNTRY[code] !== actual[code]);
        if (drifted.length === 0) return;

        console.warn(
            `[provider-counts] ${drifted.length} of ${Object.keys(actual).length} countries disagree with ` +
                `PROVIDER_COUNT_BY_COUNTRY (${drifted.join(', ')}). The provider grid will shift by up to a row ` +
                'for those. Paste this into src/data/ppoints/provider-counts.ts:\n' +
                JSON.stringify(actual, null, 4)
        );
    }, [countries]);
};

const DEFAULT_PROPS: _ShippingProviderCountriesProps = {
    useLocal: false,
};

/** Shared so an unknown provider yields a stable reference and does not defeat callers' memoisation. */
const EMPTY_FEATURES: PackageProviderFeature[] = [];

export const useShippingProviderCountries = (_props?: ShippingProviderCountriesProps) => {
    const { locations: shopifyLocations, isLoading: isLoadingLocations } = useShopifyLocationsSWR();
    const props = { ...DEFAULT_PROPS, ...(_props ?? {}) };
    const { data: countriesData, isLoading: isLoadingCountries } = useCountries();
    const { data: aggregatorsResponse, isLoading: isLoadingAggregators } = useAggregatorsSWR();
    const { settings: providerToCountry, fetching: isLoadingSettings } = useSettings<ProviderToCountrySetting>(
        SettingKey.provider_to_country
    );

    const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(null);

    const countries = useMemo<CountryWithAggregatedProviders[]>(() => {
        if (!countriesData) return [];

        return countriesData
            .map((country) => {
                const enabledProviders = country.packageproviders.filter((provider) => provider.enabled);
                const standaloneProviders = enabledProviders.filter((provider) => !provider.aggregator_id);

                // Providers that belong to an aggregator (e.g. Kvikk's own gls/dpd/packeta/...)
                // aren't shown individually — each group is collapsed into one card for the
                // aggregator itself, same as any other provider.
                const providersByAggregatorId = new Map<number, PackageProvider[]>();
                enabledProviders.forEach((provider) => {
                    if (!provider.aggregator_id) return;
                    providersByAggregatorId.set(provider.aggregator_id, [
                        ...(providersByAggregatorId.get(provider.aggregator_id) ?? []),
                        provider,
                    ]);
                });

                const aggregatorProviders = [...providersByAggregatorId.entries()]
                    .map(([aggregatorId, providers]) => {
                        const aggregator = aggregatorsResponse?.aggregators.find(
                            (candidate) => Number(candidate.id) === aggregatorId
                        );
                        return aggregator ? mapAggregatorToProvider(aggregator, providers) : undefined;
                    })
                    .filter(Boolean);

                return {
                    ...country,
                    packageproviders: [...standaloneProviders, ...aggregatorProviders],
                };
            })
            .filter((country) => country.packageproviders.length > 0)
            .sort((a, b) => (a.id > b.id ? 1 : -1));
    }, [countriesData, aggregatorsResponse]);

    const countryCodeFromLocal = useMemo(() => {
        const localeArray = shopify?.config?.locale?.split?.('-');
        return (localeArray?.length > 1 ? localeArray[1] : localeArray[0])?.toUpperCase();
    }, []);

    const shopifyLocals = useMemo(
        () => shopifyLocations?.map((l) => l.address?.countryCode.toUpperCase()),
        [shopifyLocations]
    );

    useEffect(() => {
        // `isLoadingLocations` must stay in the dependency list below: it is a guard, and an effect
        // that bails on a value it does not depend on never re-runs once that value settles, which
        // left the page with no selected country and therefore no provider cards at all.
        if (selectedCountryCode || countries.length === 0 || isLoadingSettings || isLoadingLocations) return;

        const configured = providerToCountry ?? {};
        const hasAnyProviders = Object.values(configured).some((providers) => providers.length > 0);

        const preferred =
            !hasAnyProviders || props?.useLocal
                ? countries.find((country) => shopifyLocals?.includes(country.country_code.toUpperCase()))
                : countries.find((country) => country.name === getMostProvidersCountry(configured));

        setSelectedCountryCode((preferred ?? countries[0]).country_code);
    }, [countries, selectedCountryCode, isLoadingSettings, isLoadingLocations, providerToCountry, props?.useLocal]);

    const selectedCountry = useMemo(
        () => countries.find((country) => country.country_code === selectedCountryCode) ?? null,
        [countries, selectedCountryCode]
    );

    const allProviderIds = useMemo(
        () =>
            [...new Set(countries.flatMap((country) => country.packageproviders.map((p) => p.id)))].sort(
                (a, b) => a - b
            ),
        [countries]
    );

    const { data: packagePointsProvidersFeatures, isLoading: isLoadingFeatures } = usePPointsFeatures(allProviderIds);

    /**
     * Every feature the API reports, keyed by provider id. The response carries the id as a bigint,
     * which never equals a `PackageProvider.id` number under `Map.get`, so it is narrowed once here
     * instead of at each lookup.
     */
    const featuresByProviderId = useMemo(() => {
        const byId = new Map<number, PackageProviderFeature[]>();

        packagePointsProvidersFeatures?.data?.forEach((entry) => {
            byId.set(Number(entry.packageProviderId), entry.features);
        });

        return byId;
    }, [packagePointsProvidersFeatures]);

    /**
     * Empty until the features request resolves — callers that distinguish "no features" from "not
     * loaded yet" need `isLoading`, not the length of this array.
     */
    const getProviderFeatures = useCallback(
        (providerId: number): PackageProviderFeature[] => featuresByProviderId.get(providerId) ?? EMPTY_FEATURES,
        [featuresByProviderId]
    );

    const setSelectedCountry = useCallback((country: CountryWithAggregatedProviders) => {
        setSelectedCountryCode(country?.country_code ?? null);
    }, []);

    const skeletonCount = useMemo(
        () => PROVIDER_COUNT_BY_COUNTRY[countryCodeFromLocal] ?? DEFAULT_PROVIDER_COUNT,
        [countryCodeFromLocal]
    );

    const isLoading = useMemo(
        () =>
            isLoadingCountries || isLoadingSettings || isLoadingFeatures || isLoadingLocations || isLoadingAggregators,
        [isLoadingCountries, isLoadingSettings, isLoadingFeatures, isLoadingLocations, isLoadingAggregators]
    );

    useProviderCountDriftWarning(countries, countryCodeFromLocal);

    return {
        countries,
        selectedCountry,
        selectedCountryCode,
        setSelectedCountry,
        setSelectedCountryCode,
        providerToCountry,
        packagePointsProvidersFeatures,
        getProviderFeatures,
        isLoading,
        isResolvingCountry: !selectedCountry,
        skeletonCount,
        shopifyLocals,
    };
};

interface _ShippingProviderCountriesProps {
    useLocal: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ShippingProviderCountriesProps extends Partial<_ShippingProviderCountriesProps> {}
